import React, { useState } from 'react';
import { DateFormatters } from 'app/infrastructure/data/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { OverlayComponent } from 'app/components';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { hasTranslationsValid } from 'app/components/translation/has-translations-valid';

import { getTranslationForLanguage, normalizeMessage, validateMessage } from '../utils';
import { AdministrationMessagesFormState, AdministrationMessageItem, MessagesValidation } from '../types-store';
import { AdministrationMessagesTranslation } from '../../constants';

import { MessageFormStickyHeader } from './message-form-sticky-header';

import styles from './message-form-container.scss';

interface MessageFormContainerProps {
  formState: AdministrationMessagesFormState;
  mainLanguage: number;
  canEdit: boolean;
  canDelete: boolean;

  switchTranslationsToggle: (value: boolean) => void;
  deleteMessage: (id: number, successMessage: string) => void;
  saveMessage: (message: AdministrationMessageItem,
    successMessage: string,
    validationErrorMessage: string) => void;
  updateValidation: (validation: MessagesValidation) => void;
  displayError: (errorMessage: string) => void;

  translation: AdministrationMessagesTranslation;
  formatters: DateFormatters;
  children?: React.ReactNode;
}

export const MessageFormContainer: React.FC<MessageFormContainerProps> = ({
  formState, mainLanguage, canEdit, canDelete,
  switchTranslationsToggle,
  deleteMessage, saveMessage, updateValidation, displayError,
  translation, formatters,
  children
}) => {

  const { message, saving, deleting, languageOptions } = formState;

  const [showPublishPopup, switchPublishPopup] = useState(false);

  const saveHandler = (forcePublish: boolean) => {

    saveMessage(normalizeMessage(message, forcePublish),
      isDefinedIncludingZero(message && message.id)
        ? translation.message_updated
        : translation.message_created,
      translation.message_validation_error);
  };

  const onSave = () => {
    const formValidation = validateMessage(message, languageOptions, translation.must_be_filled_out);

    updateValidation(formValidation);

    if (formValidation !== undefined) {
      !hasTranslationsValid(formValidation?.translations, mainLanguage) && switchTranslationsToggle(true);
      displayError(translation.message_validation_error);
      return;
    }

    if (message && !isDefinedIncludingZero(message.id) && !message.active) {
      switchPublishPopup(true);
      return;
    }

    saveHandler(false);
  };

  const onClose = () => {
    switchPublishPopup(false);
  };

  const onSaveAndPublish = () => {
    onClose();
    saveHandler(true);
  };

  const onSaveAndPublishLater = () => {
    onClose();
    saveHandler(false);
  };

  const onDelete = () => {
    isDefinedIncludingZero(message && message.id) && deleteMessage(message && message.id,
      translation.message_deleted);
  };

  const mainLanguageTranslation = getTranslationForLanguage(message, mainLanguage);

  return <>
    <OverlayComponent
      displayOverlay={saving || deleting}
      overlayText={saving
        ? translation.saving_message
        : translation.deleting_message} />
    {showPublishPopup
      ? <ModalWrapper
        headerTitle={translation.publish_message}
        btnCancelTitle={translation.cancel}
        btnActionTitle={translation.publish_later}
        btnTitle={translation.publish_now}
        onClose={onClose}
        onAction={onSaveAndPublishLater}
        onDone={onSaveAndPublish}
        largeSize={false}>
        <p className={styles.text}>
          {translation.publish_now_or_later}
        </p>
      </ModalWrapper>
      : null}
    <MessageFormStickyHeader
      message={message}
      language={mainLanguageTranslation}
      canEdit={canEdit}
      canDelete={canDelete}
      disabled={formState.fetching}
      onSave={onSave}
      onDelete={onDelete}
      translation={translation}
      formatters={formatters} />
    {children}
  </>;
};
